// パン中であることを文書ルート（<html>）の属性として反映するフック。
//
// キャンバスコンテナの subtree は canvas-cursor-grabbing クラスでカーソルを固定できるが、
// コメントレイヤー（CanvasStage の兄弟）やアプリメニュー・ツールバー・ミニマップ、
// document.body へ portal される UI には届かない。そのため、パン中にポインタ下へそれらが来た瞬間だけ
// カーソルが grab / pointer へ切り替わり、「パンしているのに手のひらが開閉する」ちらつきが残る。
//
// usePanelSizeReserve が実測寸法を CSS 変数として documentElement へ反映しているのと同じ考え方で、
// パン状態を文書ルートの属性として 1 か所へ反映し、CSS カスケードで文書内の全要素へ効かせる。
// 要素ごとに tool を購読して個別に cursor を切り替える方式は、対象 UI が多く書き漏れが必ず出るため採らない。

import { useLayoutEffect, type RefObject } from "react";
import { useCanvasStore } from "./store/canvas-store";

/**
 * パン中であることを文書ルートへ示す属性名。styles.css はこの属性が付いている間だけ、
 * 文書内のすべての要素（コメントピンやツールバーなど、キャンバスコンテナの兄弟や portal 先を含む）の
 * カーソルを grabbing へ固定する。
 */
export const CANVAS_PANNING_ATTRIBUTE = "data-canvas-panning";

/**
 * ツールが panning の間だけ document.documentElement へ data-canvas-panning="true" を付ける。
 * カーソルは描画時に決まるため、コンテナのクラス切替と同じ描画で反映されるよう layout effect で付ける
 * （useEffect だとコンテナのクラスだけが先に変わり、兄弟要素の固定が 1 フレーム遅れる）。
 */
export function useCanvasPanningDocumentAttribute(
  /** パンのポインタキャプチャを持つキャンバスコンテナ。安全網の監視先に使う。 */
  containerRef: RefObject<HTMLElement | null>
): void {
  const panning = useCanvasStore((s) => s.tool === "panning");
  useLayoutEffect(() => {
    if (!panning) return undefined;
    const root = document.documentElement;
    root.setAttribute(CANVAS_PANNING_ATTRIBUTE, "true");
    // pointerup を伴わない pointer capture 喪失（他ハンドラによる解放・要素差し替え・OS 都合の中断）は、
    // use-pointer-mode.ts の onLostPointerCapture がパン状態を復旧して tool を戻すため、通常は
    // 下のクリーンアップで属性が外れる。ここに置く安全網はその多重防御で、復旧側が働かなかった場合でも
    // 文書全体が grabbing のまま残らないようにする（同じ属性を冪等に外すだけなので競合しない）。
    //
    // 監視先はキャンバスコンテナに限定し、さらに「コンテナ自身が持っていたキャプチャの解放」だけを見る。
    // パンのキャプチャはコンテナ自身が持つ（use-pointer-mode.ts の container.setPointerCapture）ため
    // event.target はコンテナになる。一方、オブジェクトのドラッグ（use-object-drag.ts）は子要素へ
    // キャプチャを設定し、lostpointercapture はバブルしてコンテナへも届くが、その event.target は
    // その子要素である。event.target === container の判定によって、パン継続中に別ポインタで
    // オブジェクトを離しただけで兄弟 UI の固定が解けることを防ぐ。コンテナの外（コメントピンなど。
    // CommentLayer はコンテナの兄弟）で起きた解放は、そもそもコンテナへバブルしない。
    // 通常のパン終了でも lostpointercapture は届くが、そのときは属性を外す結果が同じなので無害である。
    const container = containerRef.current;
    const onLostPointerCapture = (event: Event): void => {
      if (event.target !== container) return;
      root.removeAttribute(CANVAS_PANNING_ATTRIBUTE);
    };
    container?.addEventListener("lostpointercapture", onLostPointerCapture);
    // パン終了・アンマウントのどちらでも必ず外す（付いたままだと画面全体が grabbing のままになる）。
    return () => {
      container?.removeEventListener("lostpointercapture", onLostPointerCapture);
      root.removeAttribute(CANVAS_PANNING_ATTRIBUTE);
    };
  }, [containerRef, panning]);
}
